/**
 * Implementação do repositório de User: orquestra datasource remoto e local.
 */

import type { User } from '@/core/domain';
import type { UserRepository } from '../../domain/repositories/user-repository';
import type { UserRemoteDataSource } from '../datasources/remote';
import type { UserLocalDataSource } from '../datasources/local';

export class UserRepositoryImpl implements UserRepository {
  constructor(
    private readonly remote: UserRemoteDataSource,
    private readonly local: UserLocalDataSource
  ) {}

  async getById(id: string, options?: { forceRefresh?: boolean }): Promise<User | null> {
    if (!options?.forceRefresh) {
      const fromCache = await this.local.getById(id);
      if (fromCache != null) return fromCache;
    }
    const fromRemote = await this.remote.getById(id);
    if (fromRemote != null) await this.local.create(fromRemote).catch(() => {});
    return fromRemote;
  }

  async getUser(): Promise<User | null> {
    return this.local.getUser();
  }

  async getAll(forceRefresh = false): Promise<User[]> {
    if (!forceRefresh) {
      const fromCache = await this.local.getAll();
      if (fromCache.length > 0) return fromCache;
    }
    const fromRemote = await this.remote.getAll();
    await this.local.cacheAll(fromRemote).catch(() => {});
    return fromRemote;
  }

  async create(user: User): Promise<User> {
    const created = await this.remote.create(user);
    await this.local.create(created).catch(() => {});
    return created;
  }

  async update(id: string, user: Partial<User>): Promise<User> {
    const updated = await this.remote.update(id, user);
    await this.local.update(id, updated).catch(() => {});
    return updated;
  }

  async delete(id: string): Promise<void> {
    await this.remote.delete(id);
    await this.local.delete(id).catch(() => {});
  }
}
